import { computed, ref } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { getAnalysesCount } from "@/api/scribe";
import { hasLuckyProductSubscription } from "@/helpers/subscription";
import type { Subscription } from "@/types/Subscription";
import { couldNot } from "@/helpers/userMessages";

const DEFAULT_FREE_REPORT_LIMIT = 30;

const getLuckyReportUsageLimit = (): number => {
	const usageLimit = Number((import.meta as any).env.VITE_LUCKY_FREE_REPORT_LIMIT);
	if (!Number.isFinite(usageLimit) || usageLimit <= 0) {
		return DEFAULT_FREE_REPORT_LIMIT;
	}

	return Math.floor(usageLimit);
};

const isLuckyReportQuotaCheckEnabled = (): boolean => {
	return (
		String((import.meta as any).env.VITE_ENABLE_LUCKY_REPORT_QUOTA_CHECK || "false")
			.trim()
			.toLowerCase() === "true"
	);
};

const FREE_REPORT_LIMIT = getLuckyReportUsageLimit();

type MeContext = {
	organizationSubscriptions?: Subscription[];
	organizationSubscription?: Subscription | null;
	organization?: {
		isTestAccount?: boolean;
	};
};

const shouldBypassUsageLimit = (currentMe: MeContext): boolean => {
	const subscriptions = currentMe?.organizationSubscriptions;
	const hasAnyLuckySubscription = Array.isArray(subscriptions) && hasLuckyProductSubscription(subscriptions);
	const subscriptionAccountType = String(currentMe?.organizationSubscription?.accountType || "")
		.trim()
		.toLowerCase();

	return (
		currentMe?.organization?.isTestAccount === true ||
		currentMe?.organizationSubscription === null ||
		subscriptionAccountType === "legacy" ||
		hasAnyLuckySubscription
	);
};

export const useLuckyReportQuota = () => {
	const toast = useToast();
	const loading = ref(false);
	const totalReportsUsed = ref(0);
	const hasLuckySubscription = ref<boolean | null>(null);
	const billingRedirecting = ref(false);

	const isQuotaCheckEnabled = computed(() => isLuckyReportQuotaCheckEnabled());
	const isUsageLimited = computed(() => hasLuckySubscription.value === false);
	const hasReachedLimit = computed(() => isUsageLimited.value && totalReportsUsed.value >= FREE_REPORT_LIMIT);
	const cappedReportsUsed = computed(() => Math.min(totalReportsUsed.value, FREE_REPORT_LIMIT));
	const usageProgress = computed(() => Math.min(100, Math.round((cappedReportsUsed.value / FREE_REPORT_LIMIT) * 100)));
	const isCheckingQuota = computed(() => isQuotaCheckEnabled.value && loading.value);

	const load = async () => {
		loading.value = true;
		try {
			if (!isQuotaCheckEnabled.value) {
				hasLuckySubscription.value = true;
				totalReportsUsed.value = 0;
				return;
			}

			const currentMe = await api.getMe();
			const subscriptions = currentMe?.organizationSubscriptions;

			if (shouldBypassUsageLimit(currentMe)) {
				hasLuckySubscription.value = true;
				totalReportsUsed.value = 0;
				return;
			}

			// If subscription products are unavailable, do not block report access.
			if (!Array.isArray(subscriptions)) {
				hasLuckySubscription.value = true;
				totalReportsUsed.value = 0;
				return;
			}

			hasLuckySubscription.value = false;
			totalReportsUsed.value = await getAnalysesCount();
		} catch {
			// Fail open so users are not blocked by transient API issues.
			hasLuckySubscription.value = true;
			totalReportsUsed.value = 0;
		} finally {
			loading.value = false;
		}
	};

	const navigateToBilling = async () => {
		billingRedirecting.value = true;
		try {
			const billingPortalResponse = await api.getBillingPortalSessionUrl("subscription_update", window.location.toString());
			window.location = billingPortalResponse.url;
		} catch (error) {
			billingRedirecting.value = false;
			console.error("Failed to redirect to billing", error);
			toast.error(couldNot("open billing"));
		}
	};

	const refreshUsageCount = async () => {
		if (!isQuotaCheckEnabled.value) {
			hasLuckySubscription.value = true;
			totalReportsUsed.value = 0;
			return;
		}

		if (hasLuckySubscription.value === null) {
			await load();
			return;
		}

		if (!isUsageLimited.value) {
			return;
		}

		try {
			totalReportsUsed.value = await getAnalysesCount();
		} catch {
			// Keep previous usage if refresh fails; avoid blocking on transient errors.
		}
	};

	return {
		loading,
		totalReportsUsed,
		usageLimit: FREE_REPORT_LIMIT,
		isQuotaCheckEnabled,
		isCheckingQuota,
		isUsageLimited,
		hasReachedLimit,
		cappedReportsUsed,
		usageProgress,
		billingRedirecting,
		load,
		refreshUsageCount,
		navigateToBilling
	};
};
